import { computed, ComputedRef, ref, Ref } from "vue";

import { usePagingFilter } from "@/composables/filters/paging-filter";
import { createDefaultLogger, LogLevels } from "@/types/console-logger";

const logger = createDefaultLogger( LogLevels.ERROR );

/*****************************************************************************
 * useContentFilter — shared scaffolding for domain filter composables.
 *
 * Every content-domain filter (articles, trade alerts, webinars, sector alerts,
 * sell signals, bonus reports) previously re-declared the same 5-piece bundle
 * around its own filter chain:
 *
 *   - `loading` ref toggled inside a try/finally
 *   - `filtered*Page` derived from `filteredContentPage()`
 *   - `hasAny*Results` = `filtered.value.length > 0`
 *   - `*SearchResultsLabel` = "N noun(s) found"
 *   - a locally-defined `logger` created from the console-logger boilerplate
 *   - an optional `pageNumber = 0` reset at the top of the computed
 *
 * That scaffolding is now here. Callers supply a `computeFiltered` closure that
 * runs their domain-specific filter chain, plus a few opts that toggle the
 * historical variations (loading exposure, page reset, custom label).
 *
 * The public shape of each domain composable is preserved by aliasing
 * (`{ filtered: filteredArticles, filteredPage: filteredArticlesPage, ... }`).
 *****************************************************************************/
export type UseContentFilterOptions< T > = {
    /**
     * Singular noun used in the default search-results label and debug logs
     * (e.g. `"article"`, `"trade alert"`).
     */
    itemNoun: string;

    /**
     * Optional plural form. Defaults to `itemNoun + 's'`. Use when the plural
     * isn't just +s (`"person"` → `"people"`).
     */
    itemNounPlural?: string;

    /**
     * Optional override for the entire label. When set, `itemNoun` /
     * `itemNounPlural` are ignored for label purposes (still used for logs).
     * Use for anything more complex than "N noun(s) found" (e.g. the webinar
     * filter's "N webinars found (X upcoming, Y past)" or the bonus report
     * filter's always-plural "N reports found").
     */
    buildLabel?: ( count: number ) => string;

    /**
     * Domain-specific filter chain. Runs inside a try/finally that toggles the
     * loading flag when `exposeLoading` is true. Return the filtered array.
     */
    computeFiltered: () => T[];

    /**
     * When true, expose a `loading` ref that flips true → false around each
     * `computeFiltered` invocation. Historical behavior of the trade-alert
     * and webinar filters (the writes happen synchronously inside a computed
     * getter, so external observers never actually see `true` — kept for
     * bit-for-bit fidelity with the original code).
     */
    exposeLoading?: boolean;

    /**
     * When true, reset the paging filter's `pageNumber` to 0 at the top of
     * each `filtered` recompute. Historical behavior of the article,
     * sector-alert, and sell-signal filters.
     */
    resetPageOnRecompute?: boolean;
};

export type UseContentFilterResult< T > = {
    filtered: ComputedRef< T[] >;
    filteredPage: ComputedRef< T[] >;
    hasAnyResults: ComputedRef< boolean >;
    searchResultsLabel: ComputedRef< string >;
    /**
     * Always returned; only mutated when `exposeLoading` was set. Callers
     * that don't need it can ignore the field.
     */
    loading: Ref< boolean >;
};

export function useContentFilter< T >( options: UseContentFilterOptions< T > ): UseContentFilterResult< T > {
    const { filteredContentPage, pageNumber } = usePagingFilter();
    const loading = ref< boolean >( false );

    const filtered: ComputedRef< T[] > = computed( () => {
        if ( options.exposeLoading ) loading.value = true;
        if ( options.resetPageOnRecompute ) pageNumber.value = 0;

        try {
            logger.debug( `[ ${ options.itemNoun }-filter ].filtered -> starting computation.` );
            const result = options.computeFiltered();
            logger.debug( `[ ${ options.itemNoun }-filter ].filtered -> ${ result.length } item(s) after filtering.` );
            return result;
        } finally {
            if ( options.exposeLoading ) loading.value = false;
        }
    } );

    const filteredPage: ComputedRef< T[] > = computed( () => filteredContentPage< T >( filtered.value ) );

    const hasAnyResults: ComputedRef< boolean > = computed( () => filtered.value.length > 0 );

    const searchResultsLabel: ComputedRef< string > = computed( () => {
        const count = filtered.value.length;
        if ( options.buildLabel ) return options.buildLabel( count );
        const plural = options.itemNounPlural ?? `${ options.itemNoun }s`;
        // Historical convention: "0 article found" (single form for zero as
        // well as one) — preserved bit-for-bit from the original composables.
        return `${ count } ${ count > 1 ? plural : options.itemNoun } found`;
    } );

    return { filtered, filteredPage, hasAnyResults, searchResultsLabel, loading };
}
